import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchMobileDevices,
  clearMobileDevices,
} from 'redux/analytics/actions';

import { Loader } from 'semantic-ui-react';
import Pagination from 'components/common/Pagination';
import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import { mobileDevices as mobileDevicesFakeData } from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

import './AnalyticsMobileDevices.css';

const AnalyticsMobileDevices = ({
  date,
  mobileDevices,
  mobileDevicesError,
  fetchMobileDevices,
  clearMobileDevices,
  me,
}) => {
  const { t } = useTranslation();
  const itemsPerPage = 7; // define how many items must be on page
  const [itemsToDisplay, setItemsToDisplay] = useState({
    start: 1,
    end: itemsPerPage,
  });

  useEffect(() => {
    fetchMobileDevices(date);
    return () => {
      clearMobileDevices();
      setItemsToDisplay({ start: 1, end: itemsPerPage });
    };
  }, [date]);

  if (mobileDevicesError) {
    return null;
  }

  if (mobileDevices && Object.keys(mobileDevices).length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("Mobile Devices")} text={t("Looks like you have no data to display yet…")} />;
  }

  const handlePaginationPageClick = (date) => {
    setItemsToDisplay({
      start: date.selected * itemsPerPage + 1,
      end: date.selected * itemsPerPage + itemsPerPage,
    });
  };

  const renderTableData = (mobileDevicesData) => {
    if (!mobileDevicesData) {
      return null;
    }
    const countPages = Math.ceil(Object.keys(mobileDevicesData).length / itemsPerPage);
    return (
      <>
        <table>
          <thead>
            <tr>
              <th />
              <th />
              <th>{t("Views")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(mobileDevicesData).slice(itemsToDisplay.start - 1, itemsToDisplay.end).map((i, index) => (
              <tr
                key={`analytics_mobile_devices_table_row_key_${index}`}
              >
                <td>{index + 1}.</td>
                <td>{i}</td>
                <td className="views">
                  {mobileDevicesData[i].views &&
                    <span className="value">{mobileDevicesData[i].views}&nbsp;&nbsp;</span>
                  }
                  {mobileDevicesData[i].percent &&
                    <span className="value-percent">({mobileDevicesData[i].percent}&#37;)</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {Object.keys(mobileDevicesData).length > itemsPerPage &&
          <Pagination
            containerClassName={'pagination-wrapper'}
            activeClassName={'active'}
            pageCount={countPages}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePaginationPageClick}
          />
        }
      </>
    );
  };

  return (
    <AnalyticsSection
      className="analytics-mobile-devices__component"
      header={
        <div className="title">
          <h3>{t("Mobile Devices")}</h3>
        </div>
      }
    >
      <div className="analytics-mobile-devices-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderTableData(mobileDevicesFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderTableData(mobileDevices)}
          </>
        }
        <Loader active={!mobileDevices} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsMobileDevices.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchMobileDevices: PropTypes.func.isRequired,
  clearMobileDevices: PropTypes.func.isRequired,
  mobileDevices: PropTypes.oneOfType([PropTypes.object]),
  mobileDevicesError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
};
AnalyticsMobileDevices.defaultProps = {
  mobileDevices: null,
  mobileDevicesError: null,
};

const mapStateToProps = state => ({
  mobileDevices: state.analyticsReducer.mobileDevices,
  mobileDevicesError: state.analyticsReducer.mobileDevicesError,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchMobileDevices,
    clearMobileDevices,
  },
)(AnalyticsMobileDevices);
